import React, { useEffect, useMemo, useState } from "react";
import {
  Atom,
  BookOpenCheck,
  Boxes,
  ClipboardCheck,
  Layers3,
  Sparkles,
  Users,
} from "lucide-react";
import logo from "../../assets/logo.jpeg"; 
import axios from "../../api/axios";

// Importing named exports directly from MasterCssClass.js
import {
  masterContainer,
  masterAmbientGradient1,
  masterAmbientGradient2,
  masterScrollArea,
  masterTopSection,
  masterBottomSection,
  masterPanelCard,
  masterGlowGradientsTop,
  masterGlowGradientsBottom,
  masterBadgeControl,
  masterBadgeLive,
  masterBadgeType,
  masterHeadingMain,
  masterHighlightText,
  masterSubText,
  masterStatGrid,
  masterStatCard,
  masterCardTopBar,
  masterCardIconContainer,
  masterValueHighlight,
  masterLabelHighlight,
  masterFeedPanel,
  masterFeedHeader,
  masterFeedScrollArea,
  masterFeedItem,
  masterFeedItemTitle,
  masterFeedItemMeta,
  masterHighlightBadge,
  masterEmptyState,
  statCardTones,
} from "../../GlobalComponents/MasterCssClass";

const formatDateTime = (value) => {
  if (!value) return "---";
  return new Date(value).toLocaleString();
};

const DashboardHome = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await axios.get("/public/dashboard-stats");
        setStats(res.data?.data || null);
      } catch {
        setStats(null);
      }
    };

    loadStats();
  }, []);

  const statCards = useMemo(
    () => [
      {
        icon: Users,
        label: "Students",
        value: stats?.counts?.users ?? 0,
        tone: statCardTones.students,
      },
      {
        icon: BookOpenCheck,
        label: "Exams",
        value: stats?.counts?.exams ?? 0,
        tone: statCardTones.exams,
      },
      {
        icon: Layers3,
        label: "Categories",
        value: stats?.counts?.categories ?? 0,
        tone: statCardTones.categories,
      },
      {
        icon: Boxes,
        label: "Subjects",
        value: stats?.counts?.subjects ?? 0,
        tone: statCardTones.subjects,
      },
      {
        icon: ClipboardCheck,
        label: "Mock Tests",
        value: stats?.counts?.mockTests ?? 0,
        tone: statCardTones.mockTests,
      },
      {
        icon: Atom,
        label: "Attempts",
        value: stats?.counts?.attempts ?? 0,
        tone: statCardTones.attempts,
      },
    ],
    [stats]
  );

  return (
    <div className={masterContainer}>
      <div className={masterAmbientGradient1} />
      <div className={masterAmbientGradient2} />

      <div className={masterScrollArea}>
        {/* TOP ROW */}
        <section className={masterTopSection}>
          <div className={masterPanelCard}>
            <div className={masterGlowGradientsTop} />
            <div className={masterGlowGradientsBottom} />

            <div className="relative z-10">
              <p className={masterBadgeControl}>
                <Sparkles size={10} className="text-[#0E3652]" />
               Dashboard
              </p>
            

              <div className={masterStatGrid}>
                {statCards.map((card) => (
                  <div key={card.label} className={masterStatCard}>
                    <div className={`${masterCardTopBar} ${card.tone}`} />
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className={masterLabelHighlight}>
                          {card.label}
                        </p>
                        <p className={masterValueHighlight}>
                          {card.value}
                        </p>
                      </div>
                      <div className={`${masterCardIconContainer} ${card.tone}`}>
                        <card.icon size={14} strokeWidth={2.5} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Core Kernel */}
          <div className={masterPanelCard}>
            <div className="relative z-10 flex h-full flex-col justify-between gap-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#D99A1C]">
                    Live Kernel
                  </p>
                  <p className="text-sm font-black text-[#0E3652] leading-none">
                    Admin Intelligence
                  </p>
                </div>
                <span className={masterBadgeLive}>Live</span>
              </div>

              <div className="rounded-xl border border-[#0E3652]/15 bg-[#F8FAFC] p-1 shadow-inner">
                <div className="flex items-center gap-2">
                  <img
                    src={logo}
                    alt="Medhashala"
                    className="h-8 w-8 rounded-lg bg-white p-0.5 shadow-sm border border-[#0E3652]/10"
                  />
                  <div>
                    <p className="text-[8px] font-black uppercase tracking-[0.15em] text-[#D99A1C]">
                      Signature
                    </p>
                    <p className="text-sm font-black text-[#0E3652] tracking-tight leading-none">
                      MEDHASHALA
                    </p>
                  </div>
                </div>

                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  <div className="rounded-xl border border-[#0E3652]/15 bg-white p-2 shadow-xs">
                    <p className="text-[8px] font-black uppercase tracking-wider text-[#D99A1C]">
                      Latest User
                    </p>
                    <p className="mt-0.5 text-xs font-black text-[#0E3652] truncate">
                      {stats?.latestUsers?.[0]?.name || "No data"}
                    </p>
                    <p className="text-[9px] font-bold text-slate-500 truncate mt-0.5">
                      {stats?.latestUsers?.[0]?.email || "---"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-[#0E3652]/15 bg-white p-2 shadow-xs">
                    <p className="text-[8px] font-black uppercase tracking-wider text-[#D99A1C]">
                      Latest Set
                    </p>
                    <p className="mt-0.5 text-xs font-black text-[#0E3652] truncate">
                      {stats?.latestMockTests?.[0]?.questionSetId || "No data"}
                    </p>
                    <p className="text-[9px] font-bold text-slate-500 truncate mt-0.5">
                      {stats?.latestMockTests?.[0]?.subjectName || "---"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BOTTOM ROW */}
        <section className={masterBottomSection}>
          {/* Panel 1 */}
          <div className={masterFeedPanel}>
            <p className={masterFeedHeader}>Latest Registered Users</p>
            <div className={masterFeedScrollArea}>
              {(stats?.latestUsers || []).length ? (
                stats.latestUsers.map((item) => (
                  <div
                    key={`${item.email}-${item.createdAt}`}
                    className={masterFeedItem}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 truncate">
                        <p className={masterFeedItemTitle}>
                          {item.name}
                        </p>
                        <p className={masterFeedItemMeta}>
                          {item.email}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className={masterHighlightBadge}>
                          Joined {formatDateTime(item.createdAt).split(",")[0]}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className={masterEmptyState}>No user metrics found.</div>
              )}
            </div>
          </div>

          {/* Panel 2 */}
          <div className={masterFeedPanel}>
            <p className={masterFeedHeader}>Latest Attempts</p>
            <div className={masterFeedScrollArea}>
              {(stats?.latestAttempts || []).length ? (
                stats.latestAttempts.map((item, index) => (
                  <div
                    key={`${item.userEmail}-${item.questionSetId}-${index}`}
                    className={masterFeedItem}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 truncate">
                        <p className={masterFeedItemTitle}>
                          {item.userName || item.userEmail || "Student"}
                        </p>
                        <p className={masterFeedItemMeta}>
                          {item.examName || item.examCode} • {item.subjectName}
                        </p>
                      </div>
                      <span className={masterBadgeType}>
                        {item.attemptType || "ATTEMPT"}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-[10px] font-mono border-t border-slate-200 pt-1">
                      <p className="font-extrabold text-[#D99A1C]">
                        {item.questionSetId}
                      </p>
                      <p className="text-[#0E3652] font-black">
                        {item.obtainedMarks ?? 0}{" "}
                        <span className="text-slate-400 font-normal">/</span>{" "}
                        {item.totalMarks ?? 0}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className={masterEmptyState}>No tracking logs.</div>
              )}
            </div>
          </div>

          {/* Panel 3 */}
          <div className={masterFeedPanel}>
            <p className={masterFeedHeader}>Latest Mock Test Sets</p>
            <div className={masterFeedScrollArea}>
              {(stats?.latestMockTests || []).length ? (
                stats.latestMockTests.map((item) => (
                  <div
                    key={`${item.questionSetId}-${item.createdAt}`}
                    className={masterFeedItem}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className={masterFeedItemTitle}>
                        {item.questionSetId}
                      </p>
                      <span className={masterHighlightBadge}>
                        Added {formatDateTime(item.createdAt).split(",")[0]}
                      </span>
                    </div>
                    <p className="text-[10px] text-[#0E3652] font-extrabold truncate mt-0.5">
                      {item.examName || "---"}
                    </p>
                    <p className={masterFeedItemMeta}>
                      {item.subjectName || "---"}
                    </p>
                  </div>
                ))
              ) : (
                <div className={masterEmptyState}>
                  No active sets found.
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardHome;